import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PlusCircle, X, Users as UsersIcon, ArrowLeft } from 'lucide-react';
import { biz } from '../../lib/api';
import { StatusBadge, PriorityBadge } from './BusinessLayout';

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', mobile: '', email: '', address: '', city: '', state: '', customer_type: 'retail' });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await biz.listCustomers(q ? { q } : {})); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name || !form.mobile) { toast.error('Name & mobile required'); return; }
    try { const c = await biz.createCustomer(form); toast.success(`Customer ${c.customer_number} saved`); setShowForm(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  return (
    <div className="grid gap-3" data-testid="biz-customers-root">
      <div className="flex items-center gap-2 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-black text-[#0D47A1]">Customer Master</div>
          <h1 className="font-heading font-black text-2xl md:text-3xl">Customers</h1>
        </div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <input placeholder="Search name / mobile / CUST ##"
            className="rounded-lg border border-border bg-white px-3 py-1.5 text-sm w-64"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="biz-customer-search"/>
          <button className="btn-primary" onClick={() => setShowForm(true)} data-testid="biz-customer-new-btn"><PlusCircle className="w-4 h-4"/>New</button>
        </div>
      </div>

      <div className="card-soft overflow-x-auto" data-testid="biz-customers-table">
        <table className="min-w-full text-sm">
          <thead className="bg-[#FFF8EE] text-[10px] uppercase tracking-widest text-ink/60">
            <tr>{['Customer #', 'Name', 'Mobile', 'City', 'Type', ''].map((h) => <th key={h} className="text-left px-2 py-2 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="6" className="p-4 text-ink/50">Loading…</td></tr>
              : !rows.length ? <tr><td colSpan="6" className="p-4 text-ink/50">No customers.</td></tr>
                : rows.map((r) => (
                  <tr key={r.id} className="border-t border-border/60 hover:bg-[#FFF8EE]" data-testid={`biz-customer-row-${r.customer_number}`}>
                    <td className="px-2 py-1.5 font-mono text-xs"><Link to={`/business/customers/${r.id}`} className="text-[#0D47A1]">{r.customer_number}</Link></td>
                    <td className="px-2 py-1.5">{r.name} {r.company_name && <span className="text-[10px] text-ink/60">({r.company_name})</span>}</td>
                    <td className="px-2 py-1.5">{r.mobile}</td>
                    <td className="px-2 py-1.5">{r.city}</td>
                    <td className="px-2 py-1.5"><span className="pill !bg-white !text-[10px]">{r.customer_type}</span></td>
                    <td className="px-2 py-1.5"><Link to={`/business/customers/${r.id}`} className="pill !bg-white text-[10px]">Open →</Link></td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-2" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-lg bg-white rounded-2xl border border-border shadow-xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="font-heading font-bold">New Customer</div>
              <button onClick={() => setShowForm(false)}><X className="w-4 h-4"/></button>
            </div>
            <div className="p-4 grid grid-cols-2 gap-2 text-sm">
              {['name', 'mobile', 'email', 'address', 'city', 'state'].map((k) => (
                <L key={k} label={k}>
                  <input value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`biz-cust-form-${k}`}/>
                </L>
              ))}
              <L label="Customer type">
                <select value={form.customer_type} onChange={(e) => setForm({ ...form, customer_type: e.target.value })}>
                  <option value="retail">Retail</option>
                  <option value="wholesale">Wholesale</option>
                  <option value="b2b">B2B</option>
                </select>
              </L>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button className="pill !bg-white" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn-primary" onClick={save} data-testid="biz-cust-form-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { biz.customerHistory(id).then(setData).catch(() => setData(null)); }, [id]);
  if (!data) return <div className="p-6 text-ink/60">Loading…</div>;
  const { customer, activities, complaints, followups, stats } = data;
  return (
    <div className="grid gap-3" data-testid="biz-customer-detail">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/business/customers" className="pill !bg-white text-xs"><ArrowLeft className="w-3 h-3"/>Back</Link>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-black text-[#0D47A1]">{customer.customer_number}</div>
          <h1 className="font-heading font-black text-xl md:text-2xl">{customer.name}</h1>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="rounded-2xl border border-border bg-white p-3"><div className="text-[10px] uppercase tracking-widest text-ink/50">Activities</div><div className="font-heading font-black text-2xl">{stats.total_activities}</div></div>
        <div className="rounded-2xl border border-border bg-white p-3"><div className="text-[10px] uppercase tracking-widest text-ink/50">Complaints</div><div className="font-heading font-black text-2xl">{stats.total_complaints}</div></div>
        <div className="rounded-2xl border border-border bg-white p-3"><div className="text-[10px] uppercase tracking-widest text-ink/50">Open</div><div className="font-heading font-black text-2xl text-[#EF6C00]">{stats.open_complaints}</div></div>
        <div className="rounded-2xl border border-border bg-white p-3"><div className="text-[10px] uppercase tracking-widest text-ink/50">Closed</div><div className="font-heading font-black text-2xl text-[#1B5E20]">{stats.closed_complaints}</div></div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Section title="Complaints">
          {complaints.map((c) => (
            <Link key={c.complaint_number} to={`/business/complaints/${c.complaint_number}`}
              className="border border-border rounded-lg px-2 py-1.5 hover:bg-[#FFF8EE] block">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-mono text-xs">{c.complaint_number}</span>
                <StatusBadge value={c.status}/>
                <PriorityBadge value={c.priority}/>
              </div>
              <div className="text-xs text-ink/60 truncate">{c.product} — {c.description}</div>
            </Link>
          ))}
          {!complaints.length && <div className="text-xs text-ink/50">No complaints.</div>}
        </Section>
        <Section title="Activities">
          {activities.map((a) => (
            <div key={a.id} className="border border-border rounded-lg px-2 py-1.5">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-mono text-xs">{a.activity_number}</span>
                <span className="text-[10px] uppercase tracking-widest text-ink/50">{a.date} · {a.activity_type}</span>
                <StatusBadge value={a.status}/>
              </div>
              <div className="text-xs text-ink/60 truncate">{a.description}</div>
            </div>
          ))}
          {!activities.length && <div className="text-xs text-ink/50">No activities.</div>}
        </Section>
        <Section title="Follow-ups">
          {followups.map((f) => (
            <div key={f.id} className="border border-border rounded-lg px-2 py-1.5">
              <div className="flex items-center gap-2 text-sm"><span className="font-mono text-xs">{f.followup_number}</span> <StatusBadge value={f.status}/></div>
              <div className="text-xs text-ink/60">Due {f.due_date} · {f.responsible_name}</div>
              <div className="text-xs">{f.description}</div>
            </div>
          ))}
          {!followups.length && <div className="text-xs text-ink/50">No follow-ups.</div>}
        </Section>
        <Section title="Contact">
          <div className="text-sm">{customer.mobile}</div>
          <div className="text-xs text-ink/70">{customer.email}</div>
          <div className="text-xs text-ink/70">{customer.address}</div>
          <div className="text-xs text-ink/70">{customer.city} · {customer.state}</div>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="card-soft p-3">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-ink/60 mb-2">{title}</div>
      <div className="grid gap-1">{children}</div>
    </div>
  );
}

function L({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-widest text-ink/50">{label}</span>
      <span className="[&>*]:w-full [&>*]:rounded-lg [&>*]:border [&>*]:border-border [&>*]:px-2 [&>*]:py-1.5 [&>*]:bg-white">{children}</span>
    </label>
  );
}
